import { getRaw } from './api.js';
import { decryptFileBytesWithKey, base64urlDecode } from './crypto.js';
import { esc } from './app.js';

// Shared utility functions

export function humanSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
  return (bytes / 1073741824).toFixed(1) + ' GB';
}

export function relTime(iso) {
  const d = new Date(iso), now = new Date(), diff = now - d;
  if (diff < 0) {
    // Future date
    const absDiff = -diff;
    const m = Math.floor(absDiff / 60000);
    if (m < 60) return `in ${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `in ${h}h`;
    const days = Math.floor(h / 24);
    return `in ${days}d`;
  }
  const m = Math.floor(diff / 60000);
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  const days = Math.floor(h / 24);
  if (days < 30) return days + 'd ago';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function startCountdown(expiresAt) {
  const el = document.getElementById('countdown');
  const tick = () => {
    if (!el || !document.body.contains(el)) return;
    const diff = expiresAt - Date.now();
    if (diff <= 0) { el.textContent = 'Expired'; return; }
    const m = Math.floor(diff / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    el.textContent = `Expires in ${m}m ${s.toString().padStart(2, '0')}s`;
    setTimeout(tick, 1000);
  };
  tick();
}

export async function fetchAndDecryptDropPayload(dropId, rawKey, msgEl) {
  msgEl.innerHTML = '<span class="loading">Downloading...</span>';

  const ab = await getRaw(`/drops/${dropId}/blob`);
  const encrypted = new Uint8Array(ab);

  msgEl.innerHTML = '<span class="loading">Decrypting...</span>';
  const plain = decryptFileBytesWithKey(encrypted, rawKey);

  let filename = 'drop-' + dropId.slice(0, 8);
  let mimeType = 'application/octet-stream';
  let fileData = plain;

  if (plain.length > 4) {
    const headerLen = new DataView(plain.buffer, plain.byteOffset).getUint32(0);
    if (headerLen > 0 && headerLen < 10240 && 4 + headerLen <= plain.length) {
      try {
        const metaJson = new TextDecoder().decode(plain.slice(4, 4 + headerLen));
        const meta = JSON.parse(metaJson);
        if (meta.name) filename = meta.name;
        if (meta.type) mimeType = meta.type;
        fileData = plain.slice(4 + headerLen);
      } catch (e) { /* legacy drop, use defaults */ }
    }
  }

  return { filename, mimeType, fileData };
}

export function triggerBrowserDownload(filename, mimeType, fileData) {
  const blob = new Blob([fileData], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadAndDecryptDropWithKey(dropId, rawKey, msgEl) {
  const { filename, mimeType, fileData } = await fetchAndDecryptDropPayload(dropId, rawKey, msgEl);
  triggerBrowserDownload(filename, mimeType, fileData);
  return filename;
}

export async function downloadAndDecryptDrop(dropId, keyStr, msgEl) {
  const rawKey = base64urlDecode(keyStr);
  return downloadAndDecryptDropWithKey(dropId, rawKey, msgEl);
}

export function parseDropEnvelope(rawBytes) {
  const metaLen = new DataView(rawBytes.buffer, rawBytes.byteOffset).getUint32(0);
  const metaJson = new TextDecoder().decode(rawBytes.slice(4, 4 + metaLen));
  return { meta: JSON.parse(metaJson), fileBytes: rawBytes.slice(4 + metaLen) };
}

export function humanDuration(totalSeconds) {
  const s = Math.floor(totalSeconds);
  const days = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const hms = `${String(h).padStart(2,'0')}h ${String(m).padStart(2,'0')}m ${String(sec).padStart(2,'0')}s`;
  if (days >= 365) {
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    const d = days % 365 % 30;
    return `${years}y ${months}mo ${d}d ${hms}`;
  }
  if (days >= 30) {
    const months = Math.floor(days / 30);
    const d = days % 30;
    return `${months}mo ${d}d ${hms}`;
  }
  if (days > 0) return `${days}d ${hms}`;
  return hms;
}
